import { usePostUsermanagerResetMutation } from 'api/rtk-api'
import { Form, OperationButton } from 'components/core-v2'
import {
  ConfirmPassword,
  FormError,
  SomethingWentWrongFormError
} from 'components/core-v2/molecules'
import {
  ConfirmPasswordField,
  NewPassword,
  NewPasswordField
} from 'components/core-v2/molecules/fields/password'
import {
  alphanumeric,
  containsUpperCaseLetter,
  minimumAmountOfNumbers,
  minimumCharacterLength,
  validatePassword
} from 'components/core-v2/molecules/fields/validators'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { ValidationRule } from '../../validation-rule-block'
import { COPY } from '../set-up-password/constants'
import { BaseFormProps } from '../types'

const validators = {
  validate: {
    ...minimumCharacterLength(8).validate,
    ...containsUpperCaseLetter.validate,
    ...minimumAmountOfNumbers(2).validate,
    ...alphanumeric.validate
  }
}

export type ResetPasswordFields = NewPasswordField & ConfirmPasswordField

const { validation } = COPY

export const ResetPasswordForm = ({ onSuccess }: Partial<BaseFormProps>) => {
  const { token, userId } = useParams()
  const form = useForm<ResetPasswordFields>()

  const [postResetPassword, { error, isError, isLoading, isSuccess }] =
    usePostUsermanagerResetMutation()

  const is401Error = isError && 'status' in error && error.status === 401
  const isServerError = isError && !is401Error

  const onSubmit = async (values: ResetPasswordFields) => {
    postResetPassword({
      resetPasswordCommand: {
        password: values.newPassword,
        token,
        userId
      }
    })
  }

  useEffect(() => {
    if (isSuccess) onSuccess?.()
  }, [isSuccess, onSuccess])

  const [newPasswordValue, confirmPasswordValue] = form.watch(['newPassword', 'confirmPassword'])
  const arePasswordsMatching = newPasswordValue === confirmPasswordValue
  const confirmPasswordValidator = {
    validate: () => arePasswordsMatching || 'Passwords do not match'
  }
  const { hasMinimumCharacters, hasOneUppercaseLetter, hasTwoNumbers, isAlphanumeric } =
    validatePassword(newPasswordValue)

  return (
    <div>
      <FormProvider {...form}>
        <Form
          noValidate
          aria-busy={isLoading}
          name="Reset password form"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <NewPassword
            aria-describedby="password-validations"
            isDisabled={isLoading}
            validators={validators}
          />
          <ConfirmPassword
            aria-describedby="password-validations"
            isDisabled={isLoading}
            validators={confirmPasswordValidator}
          />
          <span className="mt-8 mb-1">{validation.heading}</span>
          <ValidationRule.Block id="password-validations">
            <ValidationRule.Rule isCorrect={hasMinimumCharacters} label={validation.characters} />
            <ValidationRule.Rule isCorrect={hasOneUppercaseLetter} label={validation.upperCase} />
            <ValidationRule.Rule isCorrect={hasTwoNumbers} label={validation.numbers} />
            <ValidationRule.Rule isCorrect={isAlphanumeric} label={validation.specialSymbols} />
          </ValidationRule.Block>
          <FormError className="mt-6 mb-5" isShowing={is401Error} title="Error">
            Invalid old password
          </FormError>
          <SomethingWentWrongFormError className="mt-6 mb-5" isShowing={isServerError} />
          <OperationButton
            className="mt-6"
            isDisabled={isLoading}
            isLoading={isLoading}
            operationType="progress"
            type="submit"
            width="100%"
          >
            Reset password
          </OperationButton>
        </Form>
      </FormProvider>
    </div>
  )
}
